import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../lib/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { 
  CheckCircle2, 
  Clock, 
  XCircle, 
  ChevronLeft, 
  FileCheck, 
  PenTool, 
  ShieldCheck, 
  BellRing,
  ArrowRight
} from 'lucide-react';
import BottomNav from '../components/BottomNav';
import { unsealSensitiveDoc } from '../utils/security';

export default function ApplicationProgress() {
  const { loanId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loan, setLoan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!loanId) return;

    const unsub = onSnapshot(doc(db, 'loans', loanId), (snap) => {
      if (snap.exists()) {
        setLoan(unsealSensitiveDoc({ id: snap.id, ...snap.data(), type: 'loan' }));
      }
      setLoading(false);
    });

    return () => unsub();
  }, [loanId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-stripe-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-stripe-blue"></div>
      </div>
    );
  }

  if (!loan) {
    return (
      <div className="min-h-screen bg-stripe-bg flex flex-col items-center justify-center p-6 text-center">
        <XCircle size={48} className="text-red-500 mb-4" />
        <h2 className="text-xl font-bold text-stripe-dark">Application Not Found</h2>
        <p className="text-stripe-slate mt-2">The application you are looking for does not exist or has been removed.</p>
        <button onClick={() => navigate('/dashboard')} className="btn-primary mt-6">
          Go back to Dashboard
        </button>
      </div>
    );
  }

  const steps = [
    {
      id: 'submitted',
      title: 'Application Received',
      description: 'We have successfully received your loan application profile.',
      status: 'completed',
      icon: <FileCheck className="w-5 h-5" />
    },
    {
      id: 'signature',
      title: 'Agreement Signature',
      description: loan.borrowerSignatureUrl ? 'Digital agreement signed and verified.' : 'Legal signature is required to proceed.',
      status: loan.borrowerSignatureUrl ? 'completed' : 'current',
      icon: <PenTool className="w-5 h-5" />
    },
    {
      id: 'guarantor',
      title: 'Guarantor Endorsement',
      description: loan.guarantor?.status === 'verified' 
        ? 'Guarantor identity and endorsement verified.' 
        : 'Waiting for your guarantor to complete verification.',
      status: loan.guarantor?.status === 'verified' ? 'completed' : 
              (loan.borrowerSignatureUrl ? 'current' : 'pending'),
      icon: <BellRing className="w-5 h-5" />
    },
    {
      id: 'admin',
      title: 'Internal Credit Review',
      description: loan.status === 'approved' || loan.status === 'rejected' 
        ? 'Credit assessment completed.' 
        : 'Our team is reviewing your documents and eligibility.',
      status: (loan.status === 'approved' || loan.status === 'rejected') ? 'completed' : 
              (loan.guarantor?.status === 'verified' ? 'current' : 'pending'),
      icon: <ShieldCheck className="w-5 h-5" />
    },
    {
      id: 'final',
      title: 'Final Determination',
      description: loan.status === 'approved' 
        ? 'Application approved. Credit facility is now active.' 
        : loan.status === 'rejected' 
          ? 'Application declined based on credit policy.' 
          : 'Final decision pending approval.',
      status: loan.status === 'approved' ? 'success' : 
              loan.status === 'rejected' ? 'error' : 'pending',
      icon: <CheckCircle2 className="w-5 h-5" />
    }
  ];

  return (
    <div className="min-h-screen bg-stripe-bg pb-24">
      {/* Header */}
      <header className="page-header sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-stripe-border">
        <div className="page-header-content max-w-2xl mx-auto px-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-stripe-bg rounded-full transition-colors">
              <ChevronLeft size={20} className="text-stripe-dark" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-stripe-dark tracking-tight">Application Progress</h1>
              <p className="text-[10px] font-mono text-stripe-slate uppercase tracking-widest">{loan.loanId || loanId.substring(0, 10)}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Status Card */}
        <div className={`overflow-hidden rounded-2xl border mb-8 ${
          loan.status === 'approved' ? 'bg-green-50 border-green-100' :
          loan.status === 'rejected' ? 'bg-red-50 border-red-100' :
          'bg-blue-50 border-blue-100'
        }`}>
          <div className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-60 mb-1">Current Status</p>
                <h2 className={`text-2xl font-black tracking-tight ${
                  loan.status === 'approved' ? 'text-green-700' :
                  loan.status === 'rejected' ? 'text-red-700' :
                  'text-stripe-blue'
                }`}>
                  {loan.status === 'approved' ? 'Application Success' :
                   loan.status === 'rejected' ? 'Application Rejected' :
                   'Processing Request'}
                </h2>
              </div>
              <div className={`p-3 rounded-xl ${
                loan.status === 'approved' ? 'bg-white text-green-600' :
                loan.status === 'rejected' ? 'bg-white text-red-600' :
                'bg-white text-stripe-blue'
              } shadow-sm`}>
                {loan.status === 'approved' ? <CheckCircle2 size={24} /> :
                 loan.status === 'rejected' ? <XCircle size={24} /> :
                 <Clock size={24} />}
              </div>
            </div>
            
            <p className="text-sm mt-4 text-stripe-dark/70 leading-relaxed font-medium">
              {loan.status === 'approved' 
                ? "Your funds will be disbursed to your registered bank account shortly. You can manage your repayments from the dashboard."
                : loan.status === 'rejected'
                  ? "We regret to inform you that we cannot proceed with your facility at this moment. You may try applying again after 3 months."
                  : "We've received all your information. Currently, we are performing identity and credit checks which usually take 24-48 hours."}
            </p>
          </div>
        </div>

        {/* Stepper */}
        <div className="space-y-0 relative">
          {/* Vertical line connecting steps */}
          <div className="absolute left-[21px] top-6 bottom-6 w-0.5 bg-stripe-border" />

          {steps.map((step, idx) => (
            <div key={step.id} className="relative flex gap-5 pb-10 last:pb-0">
              {/* Step Circle */}
              <div className={`relative z-10 flex items-center justify-center w-11 h-11 rounded-full border-4 border-stripe-bg transition-all duration-500 shadow-sm ${
                step.status === 'completed' ? 'bg-green-500 text-white border-green-50' :
                step.status === 'success' ? 'bg-green-500 text-white border-green-50' :
                step.status === 'error' ? 'bg-red-500 text-white border-red-50' :
                step.status === 'current' ? 'bg-stripe-blue text-white border-blue-50 animate-pulse' :
                'bg-white text-stripe-slate border-gray-100'
              }`}>
                {step.status === 'completed' ? <CheckCircle2 size={20} /> : step.icon}
              </div>

              {/* Step Content */}
              <div className="flex-1 pt-1">
                <h3 className={`text-base font-bold tracking-tight ${
                  step.status === 'completed' || step.status === 'success' ? 'text-stripe-dark' :
                  step.status === 'current' ? 'text-stripe-blue' :
                  'text-stripe-slate'
                }`}>
                  {step.title}
                </h3>
                <p className="text-sm text-stripe-slate mt-1 leading-relaxed font-medium">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Back to Home Button */}
        <div className="mt-12">
            <button 
                onClick={() => navigate('/dashboard')}
                className="w-full flex items-center justify-center gap-2 py-4 bg-white border border-stripe-border rounded-2xl font-bold text-stripe-dark hover:bg-stripe-bg transition-all shadow-sm group"
            >
                Return to Dashboard
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
        </div>
      </main>

      <BottomNav />
    </div>
  );
}
